import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Gift } from "lucide-react";
import api from "@/lib/api";
import { eur, Loading } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import WhatsAppLink from "@/components/WhatsAppLink";
import BackNav from "@/components/BackNav";

export default function Extra() {
  const [extras, setExtras] = useState(null);
  const [config, setConfig] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [selected, setSelected] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/extras?category=extra").then((r) => setExtras(r.data)).catch(() => setExtras([]));
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/customer/extras/delivery-info").then((r) => setDelivery(r.data)).catch(() => {});
  }, [user]);

  if (!extras || !config) return <Loading />;

  const fee = config.extra_exit_fee ?? 10;
  const toggle = (id) => setSelected((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  const selDocs = extras.filter((e) => selected.includes(e.id));
  const selTotal = selDocs.reduce((s, e) => s + e.price, 0);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 space-y-10" data-testid="page-extra">
      <BackNav />

      <div className="max-w-2xl">
        <p className="eyebrow mb-2">Extra</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-navy mb-4">Un pensiero in più.</h1>
        <p className="text-body leading-relaxed">
          Piccoli pensieri speciali da far recapitare durante un intervento: scegli uno o più prodotti
          e li porteremo noi, con la fotografia finale a conferma.
        </p>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {extras.map((e) => (
          <div key={e.id} className="card-soft overflow-hidden" data-testid={`extra-card-${e.id}`}>
            <img src={e.image} alt={e.name} className="w-full aspect-[4/3] object-cover" loading="lazy" />
            <div className="p-5">
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <p className="font-semibold text-navy">{e.name}</p>
                {e.quote_only ? (
                  <p className="text-xs font-bold text-[#4a7c59] uppercase tracking-wide">Su preventivo</p>
                ) : (
                  <p className="font-serif text-xl text-navy">{eur(e.price)}</p>
                )}
              </div>
              <p className="text-sm text-body leading-relaxed">{e.description}</p>
              {e.quote_only ? (
                <WhatsAppLink label="Richiedi un preventivo su WhatsApp" testid={`extra-quote-${e.id}`} className="btn-secondary !inline-flex text-sm font-semibold mt-4" />
              ) : (
                <button
                  type="button"
                  data-testid={`extra-select-${e.id}`}
                  onClick={() => toggle(e.id)}
                  className={`mt-4 w-full rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors duration-200 flex items-center justify-center gap-2 ${
                    selected.includes(e.id) ? "border-[#4a7c59] bg-[#e8f0eb] text-[#2f5d3e]" : "border-[#e2e8f0] bg-white text-navy"
                  }`}
                >
                  {selected.includes(e.id) && <Check size={15} strokeWidth={3} />}
                  {selected.includes(e.id) ? "Selezionato" : "Seleziona"}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {selDocs.length > 0 && (
        <div className="card-ivory p-6 sm:p-7 space-y-4" data-testid="extra-order-panel">
          <p className="eyebrow">Il tuo ordine Extra</p>
          <div className="space-y-1.5">
            {selDocs.map((e) => (
              <div key={e.id} className="flex justify-between text-sm text-navy gap-3">
                <span>{e.name}</span>
                <span className="font-semibold whitespace-nowrap">{eur(e.price)}</span>
              </div>
            ))}
            <div className="flex justify-between text-sm text-navy gap-3" data-testid="extra-delivery-row">
              <span>
                {delivery?.free_delivery ? "Consegna durante il tuo intervento programmato" : "Uscita dedicata per la consegna (una sola volta)"}
              </span>
              <span className="font-semibold whitespace-nowrap">
                {delivery?.free_delivery ? "Gratuita" : `+${eur(fee)}`}
              </span>
            </div>
          </div>
          <div className="border-t border-[#ece7dd] pt-3 flex justify-between items-baseline">
            <span className="text-sm font-semibold text-navy">Totale</span>
            <span data-testid="extra-order-total" className="font-serif text-2xl text-navy">
              {eur(selTotal + (delivery?.free_delivery ? 0 : fee))}
            </span>
          </div>
          {!user && (
            <p className="text-xs text-muted-soft">Per completare l'ordine ti chiederemo di accedere o registrarti: ci vuole un minuto.</p>
          )}
          {user && delivery?.free_delivery && (
            <p className="text-xs text-[#2f5d3e] bg-[#e8f0eb] rounded-xl px-3 py-2" data-testid="extra-free-delivery-note">
              Hai già un intervento programmato: la consegna è gratuita, avverrà durante la stessa visita.
            </p>
          )}
          <Link
            to={user ? `/checkout?type=extra&extras=${selected.join(",")}` : "/accedi"}
            data-testid="extra-order-cta"
            className="btn-primary w-full justify-center"
          >
            Procedi all'ordine <ArrowRight size={16} />
          </Link>
        </div>
      )}

      <div className="card-ivory p-6 sm:p-8 space-y-3" data-testid="extra-exit-fee-note">
        <div className="flex items-start gap-3">
          <Gift className="text-[#4a7c59] shrink-0 mt-0.5" size={20} />
          <p className="text-sm text-body leading-relaxed">
            Il supplemento di consegna di <span className="font-semibold text-navy">{eur(fee)}</span> si applica
            solo se acquisti uno o più Extra che richiedono un'uscita dedicata. Se hai già un intervento
            programmato, consegniamo i tuoi Extra gratuitamente durante la stessa visita, indipendentemente
            dal servizio o dall'abbonamento attivo.
          </p>
        </div>
        <p className="text-sm text-body leading-relaxed pl-8">
          Anche i prodotti delle Ricorrenze (San Valentino, Compleanno, Natale, Festa di Tutti i Santi e le
          altre festività) possono essere consegnati durante un intervento già programmato, se compatibile con
          la data della ricorrenza: paghi solo il prodotto,{" "}
          <span className="font-semibold text-navy">senza nessun supplemento di consegna</span>, con qualsiasi
          abbonamento o servizio attivo.
        </p>
      </div>

      <div className="flex gap-3 flex-wrap">
        <Link to={user ? "/checkout?type=singola" : "/accedi"} data-testid="extra-cta" className="btn-primary">
          Richiedi un intervento con Extra <ArrowRight size={16} />
        </Link>
        <Link to="/abbonamenti" data-testid="extra-sub-cta" className="btn-secondary">
          Scopri gli abbonamenti
        </Link>
      </div>
    </div>
  );
}
